import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { ApiFailure, reportScreen } from './api.ts'
import { designTokens, t } from './contracts.ts'
import { navigate } from './router.ts'
import type { Endpoint, Notice, ViewState } from './types.ts'

export function Button(props: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'secondary' | 'danger' | 'quiet' }) {
  const { tone = 'secondary', className = '', ...rest } = props
  return <button className={`button button-${tone} ${className}`} {...rest} />
}

export function LinkButton({ to, children, tone = 'secondary' }: { to: string; children: ReactNode; tone?: 'primary' | 'secondary' | 'danger' | 'quiet' }) {
  return <Button tone={tone} onClick={() => navigate(to)}>{children}</Button>
}

export function BackButton({ fallback }: { fallback: string }) {
  return <Button tone="quiet" className="back" onClick={() => history.length > 1 ? history.back() : navigate(fallback)}>{t('common.back')}</Button>
}

export function PageHeader({ title, action }: { title: string; action?: ReactNode }) {
  return <header className="page-header"><h1>{title}</h1>{action}</header>
}

export function EmptyState({ titleKey, bodyKey, action }: { titleKey: string; bodyKey?: string; action?: ReactNode }) {
  return <div className="state-card" role="status"><div className="empty-mark" aria-hidden="true" /><h2>{t(titleKey)}</h2>{bodyKey && <p>{t(bodyKey)}</p>}{action}</div>
}

export function StateView({ state, titleKey, emptyBodyKey, onRetry, parentPath = '/' }: {
  state: ViewState; titleKey: string; emptyBodyKey?: string; onRetry?: () => void; parentPath?: string
}) {
  if (state === 'loading') return <div className="state-card" role="status"><span className="spinner" />{t('common.loading')}</div>
  if (state === 'notFound' || state === 'closed') return <div className="state-card"><h2>{t(titleKey)}</h2><LinkButton to={parentPath}>{t('common.back')}</LinkButton></div>
  if (state === 'offline') return <div className="state-card"><h2>{t('common.error.offline')}</h2><Button onClick={onRetry}>{t('cta.retry')}</Button></div>
  if (state === 'full' || state === 'banned' || state === 'error' || state === 'expired') return <div className="state-card" role="alert"><h2>{t(titleKey)}</h2>{onRetry && <Button onClick={onRetry}>{t('cta.retry')}</Button>}</div>
  return <EmptyState titleKey={titleKey} bodyKey={emptyBodyKey} />
}

export function useResource<T>(screen: import('./contracts.ts').ScreenId, loader: () => Promise<T>, dependencies: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [state, setState] = useState<ViewState>('loading')
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const load = useCallback(async () => {
    if (!data) setState('loading')
    try {
      const value = await loader()
      setData(value)
      setFailure(null)
      if (Array.isArray(value)) setState(value.length ? 'ready' : 'empty')
      else if (value && typeof value === 'object' && 'items' in value && Array.isArray((value as { items: unknown[] }).items)) {
        setState((value as { items: unknown[] }).items.length ? 'ready' : 'empty')
      } else setState('ready')
    } catch (error) {
      const apiError = error instanceof ApiFailure ? error : new ApiFailure(500, { code: 'server_error', message: String(error), requestId: '', field: null, retryAfterSeconds: null })
      setFailure(apiError)
      const nextState: ViewState = apiError.status === 0 ? 'offline' : apiError.status === 404 ? 'notFound'
        : apiError.problem.code === 'banned' ? 'banned' : apiError.problem.code === 'channel_full' ? 'full'
          : apiError.problem.code === 'invalid_invite' || apiError.problem.code === 'join_closed' ? 'closed' : 'error'
      setState(nextState)
      void reportScreen(screen, apiError.problem.code)
    }
  // Dependencies are supplied by screen routes and intentionally control refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const refresh = () => { void load() }
    window.addEventListener('bellhook:refresh', refresh)
    return () => window.removeEventListener('bellhook:refresh', refresh)
  }, [load])
  return { data, setData, state, setState, failure, reload: load }
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>
}

export function FormError({ children }: { children?: ReactNode }) {
  return children ? <p className="inline-error" role="alert">{children}</p> : null
}

export function ModalFrame({ title, onCancel, children }: { title: string; onCancel?: () => void; children: ReactNode }) {
  return <section className="modal-card"><div className="modal-header"><h1>{title}</h1>{onCancel && <Button tone="quiet" onClick={onCancel}>{t('cta.cancel')}</Button>}</div>{children}</section>
}

export function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel }: {
  title: string; body?: string; confirmLabel: string; onConfirm: () => void; onCancel: () => void
}) {
  return <div className="dialog-backdrop" role="presentation" onMouseDown={onCancel}><div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" onMouseDown={(event) => event.stopPropagation()}><h2 id="confirm-title">{title}</h2>{body && <p>{body}</p>}<div className="button-row"><Button onClick={onCancel}>{t('cta.cancel')}</Button><Button tone="danger" onClick={onConfirm}>{confirmLabel}</Button></div></div></div>
}

export function Form({ onSubmit, children }: { onSubmit: (event: FormEvent<HTMLFormElement>) => void; children: ReactNode }) {
  return <form className="form-stack" onSubmit={onSubmit}>{children}</form>
}

export function Badge({ children, kind = 'neutral' }: { children: ReactNode; kind?: 'hook' | 'channel' | 'status' | 'neutral' }) {
  return <span className={`badge badge-${kind}`}>{children}</span>
}

export function Avatar({ endpoint, size = 'md' }: { endpoint: Pick<Endpoint, 'id' | 'name'>; size?: 'sm' | 'md' | 'lg' }) {
  let hue = 0
  for (const character of endpoint.id) hue = (hue * 31 + character.charCodeAt(0)) % 360
  const colorAtHue = (hex: string) => {
    const channels = [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((value) => Number.parseInt(value, 16) / 255)
    const maximum = Math.max(...channels)
    const minimum = Math.min(...channels)
    const lightness = (maximum + minimum) / 2
    const saturation = maximum === minimum ? 0 : (maximum - minimum) / (1 - Math.abs(2 * lightness - 1))
    return `hsl(${hue} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%)`
  }
  return <span className={`avatar avatar-${size}`} style={{ '--avatar-dark': colorAtHue(designTokens.color.channel), '--avatar-light': colorAtHue(designTokens.colorLight.channel) } as React.CSSProperties}>{endpoint.name.slice(0, 1).toUpperCase()}</span>
}

export function NoticeRow({ notice }: { notice: Notice }) {
  return <button className={`list-row notice-row ${notice.readAt ? '' : 'unread'}`} onClick={() => navigate(`/n/${notice.id}`)}><div className="row-main"><div className="row-title"><span>{notice.title}</span>{!notice.readAt && <span className="unread-dot" />}</div><p>{notice.body ?? notice.endpointName}</p></div><div className="row-meta"><Badge kind={notice.endpointMode}>{notice.endpointName}</Badge>{notice.muted && <span aria-label={t('channels.muted')}>{t('channels.muted')}</span>}<time>{new Date(notice.eventAt ?? notice.receivedAt).toLocaleString()}</time></div></button>
}

export function EndpointRow({ endpoint, member = false }: { endpoint: Endpoint; member?: boolean }) {
  const path = member ? `/channels/${endpoint.id}` : `/endpoints/${endpoint.id}`
  return <button className="list-row" onClick={() => navigate(path)}>{endpoint.mode === 'channel' && <Avatar endpoint={endpoint} size="sm" />}<div className="row-main"><div className="row-title"><span>{endpoint.name}</span>{endpoint.paused && <Badge>{t('endpoints.paused')}</Badge>}</div><p>{endpoint.lastDeliveryAt ? t('endpoints.lastDelivery', { time: new Date(endpoint.lastDeliveryAt).toLocaleString() }) : t('endpoints.noDeliveries')}</p></div><div className="row-meta"><Badge kind={endpoint.mode}>{t(`endpoints.mode.${endpoint.mode}`)}</Badge>{endpoint.muted && <span>{t('channels.muted')}</span>}{endpoint.memberCount !== null && <span>{t('endpoints.memberCount', { count: endpoint.memberCount })}</span>}</div></button>
}

export function Toast({ message }: { message: string | null }) {
  return message ? <div className="toast" role="status">{message}</div> : null
}
